import styled from "styled-components/macro";
import type { Process } from "types/types";
import {
  useGetPageFromPath,
  useSummaryGroup,
  useVisibleGroups,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { MACHINE_BREAKDOWN_PROCESS_NAME_LINK_WIDTH } from "../machineBreakdownChartUtils";
import { Link } from "react-router-dom";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  getLinkToHubletOrGridPage,
  VERY_LONG_NAME_LENGTH,
} from "../../Timeline/timelinesUtils";
import { HOVER_UNDERLINE_CSS } from "../../range-selectors/steppable-range-selectors/ShiftSelector/ShiftSelectorStyles";
import { BREAKPOINT_MOBILE } from "constants/constants";
import { blueButton, brightBlue } from "constants/colors";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { Tooltip } from "@mui/material";
import { useGetWorkScheduleForAProcess } from "reports/components/Reports/reportUtils";
import { useSelector } from "store/useSelector";

export function ProcessNameLink({
  process,
  processIdx,
}: {
  process: Process;
  processIdx: number;
}) {
  const ckey = useCustomerKey();
  const summaryGroupDashboardPages = useSummaryGroup();
  const visibleGroups = useVisibleGroups();
  const summaryGroupTrendsPage =
    visibleGroups.find(group => group.processes.some(p => p.id === process.id))
      ?.summaryGroup ?? "not_found";
  const { isTrendsPage } = useGetPageFromPath();
  const summaryGroup = isTrendsPage
    ? summaryGroupTrendsPage
    : summaryGroupDashboardPages;
  const { translate } = useFlightTranslation();
  const [
    {
      general: { redirectFromHubletViewPageToGridPage: shouldShowGridPage },
    },
  ] = useFlightConfig();

  const isVeryLong = process.name.length > VERY_LONG_NAME_LENGTH;
  const workScheduleForProcess = useGetWorkScheduleForAProcess(process.id);
  const workScheduleOnCurrentPage = useWorkScheduleIdOnCurrentPage();
  const allGroups = useSelector(state => state.legacy.allGroups);
  const { isAllWorkSchedulesPage } = useGetPageFromPath();
  let sg = summaryGroup;
  if (isAllWorkSchedulesPage) {
    // set summaryGroup to match the process's group
    const matchingGroup = allGroups.find(
      group =>
        group.id !== "ALL_WORKSCHEDULES_PROCESS_GROUP" &&
        group.processes.map(p => p.id).includes(process.id)
    );
    if (matchingGroup) {
      sg = matchingGroup.summaryGroup;
    }
  }
  return (
    <ProcessNameLinkStyles>
      <Tooltip
        title={`${translate("Take_me_to")} ${process.name}`}
        PopperProps={{ className: "processNameTooltip" }}
        disableInteractive={true}
      >
        <div className={`processName${isVeryLong ? " isVeryLong" : ""}`}>
          <Link
            to={getLinkToHubletOrGridPage({
              ckey,
              shouldShowGridPage,
              summaryGroup: sg,
              detailKeyForProcess: String(processIdx),
              workScheduleForProcess:
                workScheduleForProcess ?? workScheduleOnCurrentPage,
            })}
          >
            {process.name}
          </Link>
        </div>
      </Tooltip>
    </ProcessNameLinkStyles>
  );
}
const ProcessNameLinkStyles = styled.div`
  width: ${MACHINE_BREAKDOWN_PROCESS_NAME_LINK_WIDTH}px;
  a {
    text-decoration: none;
  }
  &:hover {
    .processName {
      color: ${brightBlue};

      &:after {
        width: 100%;
        left: 0%;
      }
    }
  }
  .processName {
    letter-spacing: 0px;
    line-height: 1em;
    width: fit-content;
    height: fit-content;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    &.isVeryLong {
      font-size: 12px;
    }
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      font-size: 17px;
      &.isVeryLong {
        font-size: 14px;
      }
    }
    text-align: left;
    text-transform: none;
    position: relative;
    ${HOVER_UNDERLINE_CSS}
    &:after {
      border-bottom: 1px solid ${blueButton};
    }
    color: ${blueButton};
  }
`;
